import { BOOLEAN, NUMBER } from '../../../utils/component-validations';
import { concat } from '@ember/helper';
import { getDefaultValueFromMetadata } from '../../../utils/spds/metadata';
import { htmlSafe } from '@ember/template';
import { warn } from '@ember/debug';
import FlexRow from '../layout/flex/row.gjs';
import Super from './base.gjs';
import styles from './progress-bar.module.scss';

const METADATA = {
  component: 'Spds::Core::ProgressBar',
  description:
    'Reusable progress bar. Used in Spds::Modal::FullScreen. Sets width of progress bar based on (currentStep / stepCount) * 100',
  name: 'Progress Bar',
  meta: {
    absolute: {
      defaultValue: true,
      description: 'Whether the progress bar should use absolute positioning',
      type: BOOLEAN,
    },
    currentStep: {
      defaultValue: 0,
      description: 'The current step number in the progress sequence',
      initialValue: 1,
      type: NUMBER,
    },
    stepCount: {
      defaultValue: 0,
      description: 'The total number of steps in the progress sequence',
      initialValue: 4,
      type: NUMBER,
    },
  },
  resources: {
    github:
      'https://github.com/simplepractice/ember-simplepractice/blob/staging/addon/src/components/spds/core/progress-bar.gjs',
  },
};

export default class SpdsCoreProgressBar extends Super {
  static metadata = this.setMetadata(METADATA);
  baseClass = 'spds-progress-bar-container';
  componentName = 'Spds::Core::ProgressBar';
  styles = styles;

  get absolute() {
    return this.args.absolute ?? getDefaultValueFromMetadata(this.constructor.metadata, 'absolute');
  }

  get currentStep() {
    return (
      this.args.currentStep ?? getDefaultValueFromMetadata(this.constructor.metadata, 'currentStep')
    );
  }

  get stepCount() {
    return (
      this.args.stepCount ?? getDefaultValueFromMetadata(this.constructor.metadata, 'stepCount')
    );
  }

  get progressPercentage() {
    let { currentStep, stepCount } = this;

    if (stepCount < 1) return '0%';

    warn(
      `currentStep (${currentStep}) should be lower than stepCount (${stepCount})`,
      currentStep <= stepCount,
      {
        id: 'spds-progress-bar.current-step-out-of-range',
      }
    );

    return `${(currentStep / stepCount) * 100}%`;
  }

  <template>
    <FlexRow
      @absolute={{this.absolute}}
      @fitContentHeight={{true}}
      class={{this.classes}}
      data-test-spds-progress-bar-container
      {{this.validateArguments}}
      ...attributes
    >
      <FlexRow
        class="spds-progress-bar"
        data-test-spds-progress-bar
        style={{htmlSafe (concat "width: " this.progressPercentage)}}
      />
    </FlexRow>
  </template>
}

export const SPDS_CORE_PROGRESS_BAR_METADATA = SpdsCoreProgressBar.metadata;
